import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowLeft, Instagram, Linkedin, Plus, Minus } from "lucide-react";
import BackButton from "@/components/BackButton";
import PageHeader from "@/components/PageHeader";
import { cleanPolicyText } from "@/utils/policyText";

const CATEGORIES = [
  {
    n: "01",
    title: "Orders",
    items: [
      {
        q: "What is your return policy?",
        a: [
          "All The Black House collections are produced in limited quantities. Because every drop is exclusive, all sales are final.",
          "Returns or exchanges are accepted only if you receive a defective product, the wrong item, or your order is damaged during transit.",
          "Any issue must be reported within 48 hours of delivery with clear photos and an unboxing video.",
        ],
      },
      {
        q: "Can I cancel my order?",
        a: [
          "Orders may be cancelled only before they are dispatched. Once your order has been shipped, cancellations are no longer possible due to our limited production model.",
        ],
      },
      {
        q: "Can I change my shipping address after placing my order?",
        a: [
          "If your order has not been packed or shipped, we may be able to update your address. Please contact us immediately after placing your order.",
        ],
      },
      {
        q: "Can I change the size after placing my order?",
        a: [
          "Size changes are only possible before your order enters production or dispatch. Once shipped, sizes cannot be exchanged.",
          "Please refer to our Size Guide carefully before ordering.",
        ],
      },
      {
        q: "Can I combine multiple orders?",
        a: [
          "Unfortunately, no. Each order is processed separately to ensure accurate inventory tracking and faster dispatch.",
        ],
      },
      {
        q: "What is the status of my order?",
        a: [
          "You'll receive email and SMS updates throughout your order journey. You can also track your order from your account on theblackhouse.in.",
        ],
      },
    ],
  },
  {
    n: "02",
    title: "Payments",
    items: [
      {
        q: "Which payment methods do you accept?",
        a: [
          "We accept UPI, credit cards, debit cards, net banking, wallets, and Shop Pay (where enabled). All payments are securely processed.",
        ],
      },
      {
        q: "Is Cash on Delivery available?",
        a: [
          "Cash on Delivery may be available for selected pin codes. Availability will be shown during checkout.",
        ],
      },
      {
        q: "Is my payment secure?",
        a: [
          "Yes. Every transaction is encrypted using industry-standard SSL security. Your card details are never stored by The Black House.",
        ],
      },
    ],
  },
  {
    n: "03",
    title: "Shipping",
    items: [
      {
        q: "How much does shipping cost?",
        a: [
          "Free shipping on prepaid orders above ₹999. Standard shipping charges apply below ₹999. Charges are calculated automatically at checkout.",
        ],
      },
      {
        q: "How long does delivery take?",
        a: [
          "Orders are dispatched within 1–3 business days. Estimated delivery: Metro cities 2–5 days · Other cities 3–7 days · Remote areas 5–10 days.",
        ],
      },
      {
        q: "Do you ship internationally?",
        a: [
          "International shipping will be available soon. Sign up for updates to be notified first.",
        ],
      },
      {
        q: "Will sold-out products be restocked?",
        a: [
          "Usually not. Every release is produced in limited quantities. Once a product sells out, it may never return.",
        ],
      },
    ],
  },
  {
    n: "04",
    title: "Returns",
    items: [
      {
        q: "Can I exchange my T-shirt?",
        a: [
          "No. Because each drop is limited edition, exchanges for size or colour are not available.",
        ],
      },
      {
        q: "What if I receive a defective product?",
        a: [
          "Please email us within 48 hours of delivery with your order number, clear images and an unboxing video. Our team will inspect the issue and offer a replacement, store credit, or refund where applicable.",
        ],
      },
      {
        q: "How long do refunds take?",
        a: [
          "Approved refunds are processed within 5–7 business days after verification.",
        ],
      },
    ],
  },
  {
    n: "05",
    title: "Contact",
    items: [
      {
        q: "How can I contact The Black House?",
        a: [
          "Email: support@theblackhouse.in · Website: www.theblackhouse.in · Business Hours: Monday–Saturday, 10:00–19:00 IST.",
        ],
      },
      {
        q: "Where is The Black House based?",
        a: [
          "The Black House is proudly designed and operated from Faridabad, Haryana, India.",
        ],
      },
      {
        q: "Why are your collections limited?",
        a: [
          "We believe exclusivity creates value. Instead of mass production, every release is manufactured in small quantities to maintain quality, originality and uniqueness.",
        ],
      },
    ],
  },
];

function QA({ id, q, a, open, onToggle }) {
  return (
    <div
      className="border-b border-[#141414]"
      data-testid={`faq-item-${id}`}
    >
      <button
        onClick={onToggle}
        aria-expanded={open}
        data-testid={`faq-toggle-${id}`}
        className="w-full flex items-center justify-between gap-6 py-6 md:py-7 text-left group"
      >
        <h3
          className="font-display font-bold uppercase text-white text-[14px] md:text-[17px] leading-[1.25] pr-4"
          style={{ letterSpacing: "0.08em" }}
        >
          {cleanPolicyText(q)}
        </h3>
        <span
          className={
            "shrink-0 w-8 h-8 flex items-center justify-center border transition-colors duration-500 " +
            (open
              ? "border-white/70 text-white"
              : "border-[#1a1a1a] text-[#7a7a7a] group-hover:border-white/40 group-hover:text-white")
          }
          aria-hidden
        >
          {open ? (
            <Minus size={13} strokeWidth={1.4} />
          ) : (
            <Plus size={13} strokeWidth={1.4} />
          )}
        </span>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            key="content"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden"
          >
            <div className="pb-7 md:pb-8 pr-14 space-y-3 font-serif-editorial text-[15px] md:text-[17px] leading-[1.85] text-[#c9c9c4] max-w-2xl">
              {a.map((line, i) => (
                <p key={i}>{cleanPolicyText(line)}</p>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function FAQ() {
  useEffect(() => {
    document.title = "FAQ — THE BLACK HOUSE";
    window.scrollTo(0, 0);
  }, []);
  const [openKey, setOpenKey] = useState(null);
  const ease = [0.16, 1, 0.3, 1];

  return (
    <main
      data-testid="faq-page"
      className="relative bg-black text-white overflow-x-hidden"
    >
      <PageHeader />
      <BackButton />
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 0.3 }}
        className="absolute top-36 left-5 md:top-40 md:left-10 z-10 font-mono-ui text-[9px] md:text-[10px] tracking-[0.35em] text-[#5a5a5a] uppercase"
      >
        Support · FAQ
      </motion.div>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 0.3 }}
        className="absolute top-36 right-5 md:top-40 md:right-10 z-10 font-mono-ui text-[9px] md:text-[10px] tracking-[0.35em] text-[#5a5a5a] uppercase text-right"
      >
        Volume I · MMXXVI
      </motion.div>

      <header className="max-w-4xl mx-auto px-6 md:px-10 pt-40 md:pt-44 pb-8 md:pb-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.1, ease }}
          className="flex flex-col items-center text-center"
        >
          <p className="font-mono-ui text-[10px] tracking-[0.4em] uppercase text-[#6a6a6a] mt-0">
            — Frequently Asked
          </p>
          <h1
            data-testid="faq-title"
            className="font-display font-bold text-white uppercase mt-4 text-[10vw] sm:text-[6vw] md:text-[3.8vw] leading-[0.95]"
            style={{ letterSpacing: "0.18em" }}
          >
            Answers, Quietly Kept
          </h1>
          <div className="w-16 h-px bg-white/60 mt-8" />
          <p className="font-serif-editorial italic text-[#c9c9c4] max-w-xl mt-6 leading-[1.7] text-[15px] md:text-[17px]">
            {cleanPolicyText("The essentials on orders, payments, shipping and everything in between. If your question isn't here, write to us — we answer personally.")}
          </p>
        </motion.div>
      </header>

      {/* Categories */}
      <section className="max-w-4xl mx-auto px-6 md:px-10 pb-8 md:pb-10 space-y-10 md:space-y-14">
        {CATEGORIES.map((cat) => (
          <motion.div
            key={cat.n}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-8%" }}
            transition={{ duration: 0.9, ease }}
            data-testid={`faq-category-${cat.n}`}
          >
            <div className="flex items-baseline justify-between mb-6 md:mb-8">
              <div className="flex items-baseline gap-4 md:gap-6">
                <span className="font-mono-ui text-[10px] md:text-[11px] tracking-[0.35em] text-[#7a7a7a]">
                  § {cat.n}
                </span>
                <h2
                  className="font-display font-bold uppercase text-white text-[18px] md:text-[24px]"
                  style={{ letterSpacing: "0.22em" }}
                >
                  {cat.title}
                </h2>
              </div>
              <span className="font-mono-ui text-[9px] md:text-[10px] tracking-[0.3em] uppercase text-[#4a4a4a]">
                {cat.items.length} Q&A
              </span>
            </div>
            <div className="border-t border-[#141414]">
              {cat.items.map((item, i) => {
                const key = `${cat.n}-${i}`;
                return (
                  <QA
                    key={key}
                    id={key}
                    q={item.q}
                    a={item.a}
                    open={openKey === key}
                    onToggle={() => setOpenKey(openKey === key ? null : key)}
                  />
                );
              })}
            </div>
          </motion.div>
        ))}
      </section>

      {/* Closing motto */}
      <section className="max-w-4xl mx-auto px-6 md:px-10 pb-0">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-10%" }}
          transition={{ duration: 1, ease }}
          className="border-t border-[#141414] pt-10 md:pt-12 text-center"
        >
          <p className="font-serif-editorial italic text-white text-3xl md:text-5xl leading-[1.15] max-w-3xl mx-auto">
            "Own the Few. Not the Many."
          </p>
          <p className="font-mono-ui text-[10px] tracking-[0.35em] uppercase text-[#5a5a5a] mt-6">
            — The Black House
          </p>
        </motion.div>
      </section>

      {/* Footer */}
      <footer className="border-t border-[#141414]">
        <div className="max-w-4xl mx-auto px-6 md:px-10 py-4 md:py-5 flex flex-col md:flex-row items-center justify-between gap-6">
          <Link
            data-testid="faq-back-home"
            to="/"
            className="group inline-flex items-center gap-2 font-mono-ui text-[10px] md:text-[11px] tracking-[0.3em] uppercase text-[#8a8a8a] hover:text-white transition-colors duration-500"
          >
            <ArrowLeft size={13} strokeWidth={1.4} className="transition-transform duration-500 group-hover:-translate-x-1" />
            Return To The House
          </Link>
          <div className="flex items-center gap-3 flex-wrap justify-center">
            <Link to="/about" className="font-mono-ui text-[10px] md:text-[11px] tracking-[0.3em] uppercase text-[#8a8a8a] hover:text-white transition-colors duration-500">About</Link>
            <span className="text-[#2a2a2a]">·</span>
            <Link to="/shipping" className="font-mono-ui text-[10px] md:text-[11px] tracking-[0.3em] uppercase text-[#8a8a8a] hover:text-white transition-colors duration-500">Shipping</Link>
            <span className="text-[#2a2a2a]">·</span>
            <Link to="/terms" className="font-mono-ui text-[10px] md:text-[11px] tracking-[0.3em] uppercase text-[#8a8a8a] hover:text-white transition-colors duration-500">Terms</Link>
            <span className="text-[#2a2a2a]">·</span>
            <Link to="/privacy" className="font-mono-ui text-[10px] md:text-[11px] tracking-[0.3em] uppercase text-[#8a8a8a] hover:text-white transition-colors duration-500">Privacy</Link>
            <span className="text-[#2a2a2a]">·</span>
            <a href="https://www.instagram.com/theblackhouse.tbh" target="_blank" rel="noreferrer noopener" aria-label="Instagram" className="group w-9 h-9 flex items-center justify-center border border-[#1a1a1a] hover:border-white/70 transition-colors duration-500">
              <Instagram size={13} strokeWidth={1.4} className="text-white/70 group-hover:text-white transition-colors duration-500" />
            </a>
            <a href="https://www.linkedin.com/company/the-black-house/" target="_blank" rel="noreferrer noopener" aria-label="LinkedIn" className="group w-9 h-9 flex items-center justify-center border border-[#1a1a1a] hover:border-white/70 transition-colors duration-500">
              <Linkedin size={13} strokeWidth={1.4} className="text-white/70 group-hover:text-white transition-colors duration-500" />
            </a>
          </div>
        </div>
        <div className="border-t border-[#141414] px-6 md:px-10 py-3 flex items-center justify-between font-mono-ui text-[8px] md:text-[9px] tracking-[0.3em] uppercase text-[#3a3a3a]">
          <span>© MMXXVI — THE BLACK HOUSE</span>
          <span>All rights are gathered.</span>
        </div>
      </footer>
    </main>
  );
}
